import React from 'react';
import { Box } from '@mui/material';

export default function DashboardMockup() {
  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08)',
        background: '#111827',
        userSelect: 'none',
      }}
    >
      {/* Browser chrome */}
      <Box sx={{
        background: '#1f2937',
        px: 2,
        py: 1.2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Box sx={{ display: 'flex', gap: 0.6 }}>
          {['#ff5f57', '#ffbd2e', '#28c940'].map((c) => (
            <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', background: c }} />
          ))}
        </Box>
        <Box sx={{
          flex: 1,
          background: 'rgba(255,255,255,0.06)',
          borderRadius: 1,
          height: 22,
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
        }}>
          <Box sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>
            app.neodrive.com/reports
          </Box>
        </Box>
      </Box>

      {/* App content */}
      <Box sx={{ display: 'flex', height: 360 }}>
        {/* Sidebar */}
        <Box sx={{
          width: 52,
          background: '#1a2332',
          borderRight: '1px solid rgba(255,255,255,0.05)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 2,
          gap: 2,
        }}>
          {/* Logo icon in sidebar */}
          <Box sx={{
            width: 30, height: 30, borderRadius: '8px',
            background: 'linear-gradient(135deg, #16a34a, #ea580c)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mb: 1,
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16">
              <path d="M 5 6.5 L 8 4.5 L 11 6.5 L 11 9.5 L 8 11.5 L 5 9.5 Z" fill="none" stroke="white" strokeWidth="1.2" strokeLinejoin="round"/>
              <line x1="8" y1="4.5" x2="8" y2="11.5" stroke="white" strokeWidth="1"/>
              <line x1="5" y1="6.5" x2="11" y2="9.5" stroke="white" strokeWidth="1"/>
            </svg>
          </Box>
          {[
            { path: 'M 10 3 L 14 3 M 2 3 L 7 3 M 2 8 L 14 8 M 2 13 L 14 13', label: 'nav' },
            { path: 'M 3 3 L 13 3 L 13 13 L 3 13 Z', label: 'box' },
            { path: 'M 8 3 L 8 13 M 3 8 L 13 8', label: 'plus' },
            { path: 'M 3 8 A 5 5 0 1 1 13 8', label: 'arc' },
          ].map((icon, i) => (
            <Box
              key={i}
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                background: i === 0 ? 'rgba(22,163,74,0.2)' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={i === 0 ? '#4ade80' : 'rgba(255,255,255,0.3)'} strokeWidth="1.2">
                <path d={icon.path} />
              </svg>
            </Box>
          ))}
        </Box>

        {/* Main content */}
        <Box sx={{ flex: 1, p: 2, overflow: 'hidden' }}>
          {/* Header row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Box sx={{ height: 10, width: 120, background: 'rgba(255,255,255,0.7)', borderRadius: 1, mb: 0.8 }} />
              <Box sx={{ height: 7, width: 80, background: 'rgba(255,255,255,0.2)', borderRadius: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box sx={{ height: 22, width: 60, background: 'rgba(22,163,74,0.25)', borderRadius: 1, border: '1px solid rgba(22,163,74,0.4)' }} />
              <Box sx={{ height: 22, width: 70, background: 'linear-gradient(135deg, #16a34a, #15803d)', borderRadius: 1 }} />
            </Box>
          </Box>

          {/* Stat cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.2, mb: 2 }}>
            {[
              { value: '247', label: 'Estafetas', color: '#4ade80', bg: 'rgba(22,163,74,0.12)' },
              { value: '18,4 k€', label: 'Esta semana', color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
              { value: '98%', label: 'Pago', color: '#fb923c', bg: 'rgba(234,88,12,0.1)' },
            ].map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  background: stat.bg,
                  border: `1px solid ${stat.color}25`,
                  borderRadius: 1.5,
                  p: 1.2,
                }}
              >
                <Box sx={{ fontSize: '0.7rem', color: stat.color, fontWeight: 700, mb: 0.3 }}>
                  {stat.value}
                </Box>
                <Box sx={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.04em' }}>
                  {stat.label}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Report table */}
          <Box sx={{
            background: 'rgba(255,255,255,0.04)',
            borderRadius: 1.5,
            border: '1px solid rgba(255,255,255,0.06)',
            overflow: 'hidden',
          }}>
            {/* Table header */}
            <Box sx={{ display: 'flex', px: 1.5, py: 1, borderBottom: '1px solid rgba(255,255,255,0.06)', gap: 1 }}>
              {['Estafeta', 'Parceiro', 'Receita', 'Comissão', 'Estado', ''].map((h) => (
                <Box key={h || 'spacer'} sx={{ flex: h === 'Estafeta' ? 2 : 1, height: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 1 }} />
              ))}
            </Box>
            {/* Table rows */}
            {[
              { colors: ['#4ade80', '#60a5fa', '#e2e8f0', '#fb923c', '#4ade80'] },
              { colors: ['#94a3b8', '#60a5fa', '#e2e8f0', '#fb923c', '#4ade80'] },
              { colors: ['#94a3b8', '#60a5fa', '#e2e8f0', '#fb923c', '#fbbf24'] },
              { colors: ['#94a3b8', '#60a5fa', '#e2e8f0', '#fb923c', '#4ade80'] },
              { colors: ['#94a3b8', '#60a5fa', '#e2e8f0', '#fb923c', '#4ade80'] },
            ].map((row, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  px: 1.5,
                  py: 0.9,
                  gap: 1,
                  borderBottom: i < 4 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                  '&:hover': { background: 'rgba(255,255,255,0.03)' },
                }}
              >
                <Box sx={{ flex: 2, display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Box sx={{
                    width: 16, height: 16, borderRadius: '50%',
                    background: `rgba(${i * 30}, ${180 - i * 20}, ${100 + i * 20}, 0.3)`,
                    border: `1px solid rgba(${i * 30}, ${180 - i * 20}, ${100 + i * 20}, 0.5)`,
                    flexShrink: 0,
                  }} />
                  <Box sx={{ height: 6, width: `${50 + i * 12}%`, background: row.colors[0] + '60', borderRadius: 1 }} />
                </Box>
                {row.colors.slice(1).map((color, j) => (
                  <Box key={j} sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    {j === 3 ? (
                      <Box sx={{
                        height: 14, width: '70%',
                        background: color === '#4ade80' ? 'rgba(74,222,128,0.2)' : 'rgba(251,191,36,0.2)',
                        border: `1px solid ${color}40`,
                        borderRadius: 1,
                      }} />
                    ) : (
                      <Box sx={{ height: 5, width: '75%', background: color + '40', borderRadius: 1 }} />
                    )}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Bottom status bar */}
      <Box sx={{
        background: '#1f2937',
        px: 2,
        py: 0.8,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTop: '1px solid rgba(255,255,255,0.05)',
      }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80' }} />
          <Box sx={{ height: 5, width: 80, background: 'rgba(255,255,255,0.2)', borderRadius: 1 }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[60, 45, 70].map((w, i) => (
            <Box key={i} sx={{ height: 5, width: w, background: 'rgba(255,255,255,0.1)', borderRadius: 1 }} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
